import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Edit, Save, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import api from '../../utils/api';

const ServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchServices = useCallback(async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const startEdit = (service) => {
    setEditingId(service.id);
    setEditForm(service);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    try {
      await api.put(`/services/${editingId}`, editForm);
      fetchServices();
      setEditingId(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating service:', error);
      alert('Hizmet güncellenirken hata oluştu');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const iconOptions = ['Code', 'Brain', 'Globe', 'Database', 'Settings', 'Cpu', 'Shield', 'Zap', 'Layers', 'Cloud'];

  return (
    <div data-testid="admin-services">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Hizmetler</h1>
        <p className="text-gray-400">Hizmetleri düzenleyin</p>
      </div>

      {loading ? (
        <div className="text-gray-400">Yükleniyor...</div>
      ) : (
        <div className="space-y-4">
          {services.map((service, index) => {
            const IconComponent = Icons[service.icon] || Icons.Code;
            const isEditing = editingId === service.id;

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-6 rounded-2xl glass"
              >
                {isEditing ? (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Başlık (TR)</label>
                        <input
                          type="text"
                          name="title_tr"
                          value={editForm.title_tr}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                   focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Title (EN)</label>
                        <input
                          type="text"
                          name="title_en"
                          value={editForm.title_en}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                   focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Kısa Açıklama (TR)</label>
                        <textarea
                          name="short_desc_tr"
                          value={editForm.short_desc_tr}
                          onChange={handleChange}
                          rows={2}
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                   focus:border-blue-500 focus:outline-none resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Short Desc (EN)</label>
                        <textarea
                          name="short_desc_en"
                          value={editForm.short_desc_en}
                          onChange={handleChange}
                          rows={2}
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                   focus:border-blue-500 focus:outline-none resize-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Detaylı İçerik (TR) - HTML</label>
                      <textarea
                        name="content_tr"
                        value={editForm.content_tr}
                        onChange={handleChange}
                        rows={6}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                 focus:border-blue-500 focus:outline-none resize-none font-mono text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Detail Content (EN) - HTML</label>
                      <textarea
                        name="content_en"
                        value={editForm.content_en}
                        onChange={handleChange}
                        rows={6}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                 focus:border-blue-500 focus:outline-none resize-none font-mono text-sm"
                      />
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">İkon</label>
                        <select
                          name="icon"
                          value={editForm.icon}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                   focus:border-blue-500 focus:outline-none"
                        >
                          {iconOptions.map(icon => (
                            <option key={icon} value={icon}>{icon}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Sıra</label>
                        <input
                          type="number"
                          name="order"
                          value={editForm.order}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 
                                   focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            name="active"
                            checked={editForm.active}
                            onChange={handleChange}
                            className="w-5 h-5 rounded"
                          />
                          <span>Aktif</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 rounded-xl glass hover:bg-white/10 flex items-center gap-2"
                      >
                        <X className="w-4 h-4" /> İptal
                      </button>
                      <button
                        onClick={saveEdit}
                        className="px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 flex items-center gap-2"
                      >
                        <Save className="w-4 h-4" /> Kaydet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 
                                  flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{service.title_tr}</h3>
                      <p className="text-sm text-gray-400">{service.short_desc_tr}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      service.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {service.active ? 'Aktif' : 'Pasif'}
                    </span>
                    <button
                      onClick={() => startEdit(service)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                    >
                      <Edit className="w-5 h-5 text-blue-400" />
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServicesManagement;
